// ─────────────────────────────────────────────────────────────
// Bunny Stream — Client-side Token Generation
// Eliminates the backend round-trip (cold-start + Google identity check)
// by generating the SHA-256 signed embed URL directly in the browser.
// ─────────────────────────────────────────────────────────────

const BUNNY_STREAM_LIBRARY_ID = '639923';
const BUNNY_STREAM_TOKEN_KEY  = '8e02b8f3-2490-47c2-ae1a-f70db55dc2ed';

/**
 * SHA-256 hash via Web Crypto API (native, no library needed).
 * @param {string} message
 * @returns {Promise<string>} hex digest
 */
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a signed Bunny Stream embed URL instantly (no backend needed).
 * @param {string} videoId  — Bunny Stream video GUID
 * @param {object} options  — { autoplay: boolean }
 * @returns {Promise<string>} signed iframe embed URL
 */
export async function generateStreamUrl(videoId, { autoplay = true } = {}) {
  if (!videoId) return null;
  
  const expires = Math.floor(Date.now() / 1000) + (2 * 60 * 60); // 2 hours
  const hash = await sha256(BUNNY_STREAM_TOKEN_KEY + videoId + expires);
  
  let url = `https://iframe.mediadelivery.net/embed/${BUNNY_STREAM_LIBRARY_ID}/${videoId}?token=${hash}&expires=${expires}`;
  if (autoplay) url += '&autoplay=true';
  
  return url;
}

// ── URL Cache to avoid regenerating for recently visited lessons ──
const streamCache = new Map();
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

/**
 * Get a cached or freshly generated stream URL.
 * @param {string} videoId
 * @returns {Promise<string|null>}
 */
export async function getCachedStreamUrl(videoId) {
  if (!videoId) return null;
  
  const cached = streamCache.get(videoId);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.url;
  }
  
  const url = await generateStreamUrl(videoId);
  streamCache.set(videoId, { url, ts: Date.now() });
  return url;
}

/**
 * Pre-fetch a stream URL (e.g., on hover) so it's ready instantly on click.
 * @param {string} videoId
 */
export function prefetchStreamUrl(videoId) {
  if (!videoId || streamCache.has(videoId)) return;
  getCachedStreamUrl(videoId); // Fire-and-forget
}
